import { useCallback, useMemo, useRef, useState } from 'react';

import useControlledValue from '../../../utils/hooks/use-controlled-value';
import useEventCallback from '../../../utils/hooks/use-event-callback';
import useForkRefs from '../../../utils/hooks/use-fork-refs';
import useIsomorphicLayoutEffect from '../../../utils/hooks/use-isomorphic-layout-effect';
import { useFieldId } from '../../../utils/use-id';

import type { FocusEvent, ForwardedRef, KeyboardEvent, MutableRefObject, RefCallback } from 'react';

import type { SegmentedControlItem } from './segmented-control.types';

/**
 * CSS transform and width values used to position the sliding thumb or focus
 * indicator absolutely over a specific segment element.
 */
interface IndicatorStyle {
  /** CSS `translateX` value derived from the segment's `offsetLeft`. */
  transform: string;
  /** Pixel width derived from the segment's `offsetWidth`. */
  width: string;
}

/**
 * Pairs a `SegmentedControlItem` with its original array index so that
 * enabled-item filtering does not lose positional information needed for
 * DOM ref lookups and roving-tabindex calculations.
 */
interface IndexedItem {
  /** Original position of the item in the `items` array. */
  index: number;
  item: SegmentedControlItem;
}

/** Parameters accepted by the `useSegmentedControl` hook. */
interface UseSegmentedControlParameters {
  /** Initial selected value for uncontrolled usage. */
  defaultValue?: string;
  /** When `true`, all interaction is suppressed. */
  disabled: boolean;
  /** Ordered list of segment descriptors. */
  items: SegmentedControlItem[];
  /** Base name for the hidden radio inputs; auto-generated when omitted. */
  name?: string;
  /** Callback fired when the selected value changes. */
  onChange?: (value: string) => void;
  /** When `true`, the selected value is displayed but cannot be changed. */
  readOnly: boolean;
  /** Forwarded ref from the `SegmentedControl` component. */
  ref: ForwardedRef<HTMLDivElement>;
  /** Currently selected value for controlled usage. */
  value?: string;
}

/** Values returned by `useSegmentedControl` for consumption by `SegmentedControl`. */
interface UseSegmentedControlReturn {
  /** Style for the focus-ring indicator, or `null` when no segment is focused. */
  focusIndicatorStyle: IndicatorStyle | null;
  /** Capture-phase blur handler that clears the focused index when focus leaves the control. */
  handleBlur: (event: FocusEvent<HTMLDivElement>) => void;
  /** Capture-phase focus handler that tracks which segment item currently has focus. */
  handleFocus: (event: FocusEvent<HTMLDivElement>) => void;
  /** Keyboard handler supporting arrow navigation, Home/End, and Space/Enter activation. */
  handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  /** Selects a segment by value, guarded by disabled and read-only checks. */
  handleSelect: (value: string, isItemDisabled: boolean) => void;
  /** When `true`, suppresses the thumb CSS transition to prevent an unwanted initial animation. */
  isThumbTransitionDisabled: boolean;
  /** Merged ref combining the forwarded ref and the internal root ref. */
  mergedRef: null | RefCallback<HTMLDivElement>;
  /** Resolved `name` attribute shared by all hidden radio inputs in the group. */
  resolvedName: string | undefined;
  /** The currently selected segment value. */
  selectedValue: string | undefined;
  /** Stable callback to register or clear a segment element ref by its array index. */
  setItemRef: (index: number, element: HTMLDivElement | null) => void;
  /** Index of the segment that should receive `tabIndex={0}` for roving-tabindex navigation. */
  tabbableIndex: number;
  /** Style for the selected-item thumb indicator, or `null` when nothing is selected. */
  thumbStyle: IndicatorStyle | null;
}

/**
 * Returns the array index of the currently selected item, or `-1` when
 * `selectedValue` is `undefined` or does not match any item.
 */
const getSelectedIndex = (items: SegmentedControlItem[], selectedValue: string | undefined): number => {
  return selectedValue == null ? -1 : items.findIndex((item) => item.value === selectedValue);
};

/**
 * Returns all non-disabled items paired with their original array indices,
 * preserving positional information needed for DOM ref lookups.
 */
const getEnabledItems = (items: SegmentedControlItem[]): IndexedItem[] => {
  return items.map((item, index) => ({ index, item })).filter(({ item }) => !item.disabled);
};

/**
 * Returns the next index within the enabled-items list based on the pressed
 * navigation key, wrapping at the boundaries, or `null` for unhandled keys.
 *
 * @param currentEnabledIndex - Index within the enabled-items array (not the full items array).
 * @param enabledItemsLength - Total number of enabled items.
 * @param key - `KeyboardEvent.key` value (e.g. `'ArrowRight'`, `'Home'`).
 */
const getNextEnabledItemIndex = (
  currentEnabledIndex: number,
  enabledItemsLength: number,
  key: string,
): null | number => {
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight': {
      return currentEnabledIndex === -1 || currentEnabledIndex >= enabledItemsLength - 1 ? 0 : currentEnabledIndex + 1;
    }
    case 'ArrowLeft':
    case 'ArrowUp': {
      return currentEnabledIndex <= 0 ? enabledItemsLength - 1 : currentEnabledIndex - 1;
    }
    case 'End': {
      return enabledItemsLength - 1;
    }
    case 'Home': {
      return 0;
    }
    default: {
      return null;
    }
  }
};

/**
 * Returns the index (in the full items array) of the segment that should
 * receive `tabIndex={0}` for roving-tabindex keyboard navigation.
 *
 * Prefers the selected item when it is enabled; falls back to the first
 * enabled item. Returns `-1` if there are no enabled items.
 */
const getTabbableIndex = (
  items: SegmentedControlItem[],
  enabledItems: IndexedItem[],
  selectedIndex: number,
): number => {
  if (selectedIndex !== -1 && !items[selectedIndex]?.disabled) {
    return selectedIndex;
  }

  return enabledItems[0]?.index ?? -1;
};

/** Returns `true` when the key should activate (select) the currently focused segment. */
const isActivationKey = (key: string): boolean => {
  return key === ' ' || key === 'Enter';
};

/** Returns `true` when the key is one of the four arrow keys used for segment navigation. */
const isArrowNavigationKey = (key: string): boolean => {
  return key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp';
};

/**
 * Measures the segment element at `index` and returns the corresponding
 * `IndicatorStyle` for positioning the thumb or focus indicator, or `null`
 * when `index` is `-1` or the element is not yet mounted.
 */
const measureThumb = (index: number, itemRefs: MutableRefObject<(HTMLDivElement | null)[]>): IndicatorStyle | null => {
  if (index === -1) return null;

  const element = itemRefs.current[index];
  if (!element) return null;

  return {
    transform: `translateX(${element.offsetLeft}px)`,
    width: `${element.offsetWidth}px`,
  };
};

/**
 * Encapsulates all state and event-handling logic for the `SegmentedControl`
 * component, including:
 * - Controlled/uncontrolled value management via `useControlledValue`
 * - Roving-tabindex keyboard navigation
 * - Animated thumb and focus-ring indicator positioning via synchronous DOM
 *   measurements in a layout effect
 * - `ResizeObserver`-based indicator recalculation on container resize
 */
const useSegmentedControl = (parameters: UseSegmentedControlParameters): UseSegmentedControlReturn => {
  const { defaultValue, disabled, items, name, onChange, readOnly, ref, value } = parameters;

  const [selectedValue, setSelectedValue] = useControlledValue<string | undefined>({
    controlledValue: value,
    defaultValue,
    name: 'SegmentedControl',
  });

  const resolvedName = useFieldId(name, 'rbui-segmented-control');
  const rootRef = useRef<HTMLDivElement>(null);
  const mergedRef = useForkRefs(ref, rootRef);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const [focusIndicatorStyle, setFocusIndicatorStyle] = useState<IndicatorStyle | null>(null);
  const [isThumbTransitionDisabled, setIsThumbTransitionDisabled] = useState(selectedValue != null);
  const [thumbStyle, setThumbStyle] = useState<IndicatorStyle | null>(null);
  const selectedIndex = useMemo(() => getSelectedIndex(items, selectedValue), [items, selectedValue]);
  const enabledItems = useMemo(() => getEnabledItems(items), [items]);
  const tabbableIndex = useMemo(
    () => getTabbableIndex(items, enabledItems, selectedIndex),
    [items, enabledItems, selectedIndex],
  );

  const recalculateThumb = useCallback(() => {
    // Setting state from a layout effect is intentional here because the
    // indicators need synchronous DOM measurements before paint.
    // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
    setThumbStyle(measureThumb(selectedIndex, itemRefs));
    // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
    setFocusIndicatorStyle(measureThumb(focusedIndex, itemRefs));
  }, [focusedIndex, selectedIndex]);

  useIsomorphicLayoutEffect(() => {
    recalculateThumb();

    if (!isThumbTransitionDisabled) return;

    const id = requestAnimationFrame(() => {
      setIsThumbTransitionDisabled(false);
    });

    return () => cancelAnimationFrame(id);
  }, [focusedIndex, isThumbTransitionDisabled, items, selectedValue, recalculateThumb]);

  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;

    // eslint-disable-next-line unicorn/no-useless-undefined
    if (!root || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      recalculateThumb();
    });

    observer.observe(root);

    return () => observer.disconnect();
  }, [recalculateThumb]);

  const handleSelect = useEventCallback((nextValue: string, isItemDisabled: boolean) => {
    if (isItemDisabled || disabled || readOnly) return;
    if (nextValue === selectedValue) return;

    setSelectedValue(nextValue);
    onChange?.(nextValue);
  });

  const handleFocus = useEventCallback((event: FocusEvent<HTMLDivElement>) => {
    const focusTarget = event.target as HTMLDivElement | null;
    const nextFocusedIndex = focusTarget == null ? -1 : itemRefs.current.indexOf(focusTarget);

    if (nextFocusedIndex !== -1) {
      setFocusedIndex(nextFocusedIndex);
    }
  });

  const handleBlur = useEventCallback((event: FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = event.relatedTarget as Node | null;

    if (nextFocusedElement != null && rootRef.current?.contains(nextFocusedElement)) {
      return;
    }

    setFocusedIndex(-1);
  });

  const handleKeyDown = useEventCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (enabledItems.length === 0) return;

    const currentIndex = itemRefs.current.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    if (isActivationKey(event.key)) {
      event.preventDefault();

      const focusedItem = items[currentIndex];
      if (focusedItem) {
        handleSelect(focusedItem.value, disabled || !!focusedItem.disabled);
      }

      return;
    }

    const currentEnabledIndex = enabledItems.findIndex(({ index }) => index === currentIndex);
    const nextEnabledIndex = getNextEnabledItemIndex(currentEnabledIndex, enabledItems.length, event.key);
    if (nextEnabledIndex == null) return;

    event.preventDefault();

    const { index: nextIndex, item: nextItem } = enabledItems[nextEnabledIndex];
    itemRefs.current[nextIndex]?.focus();

    if (isArrowNavigationKey(event.key)) {
      handleSelect(nextItem.value, disabled || !!nextItem.disabled);
    }
  });

  const setItemRef = useCallback((index: number, element: HTMLDivElement | null) => {
    itemRefs.current[index] = element;
  }, []);

  return {
    focusIndicatorStyle,
    handleBlur,
    handleFocus,
    handleKeyDown,
    handleSelect,
    isThumbTransitionDisabled,
    mergedRef,
    resolvedName,
    selectedValue,
    setItemRef,
    tabbableIndex,
    thumbStyle,
  };
};

export default useSegmentedControl;
